import {
  DPDynamicSourceType,
  DPDynamicSourceTypeName,
  DynamicParametersType,
  SourceLayerParam,
  SourceLayerType,
} from './dynamicParameters.enums';
import iconsObj from '../../assets/icons/iconsObj';
import {SourceLayerConfig} from './dynamicParameters.types';
import {colors} from '../../themes/light/palette';
import {complexTextDynamicSourceIndicator} from '../complexText/complexText.consts';
import {WidgetFieldType} from './widgets/widgtes.enums';

const defaultMappingFontSize = 14;
const defaultMappingStrikeThrough = 1;
const clickDefaultSourceValue = `(function f(){return "default"})`;
const dynamicSourceRegexp = new RegExp(
  `${complexTextDynamicSourceIndicator}.+?${complexTextDynamicSourceIndicator}`,
  'gs',
);

const defaultComplexTextModalSize = {width: 300, height: 250};

const sourceParamIcons = new Proxy<Partial<Record<SourceLayerParam, string>>>(
  {
    [SourceLayerParam.TextSource]: iconsObj.dynamicParams.paramTypeTextSource,
    [SourceLayerParam.FillColor]: iconsObj.dynamicParams.paramTypeFillColor,
    [SourceLayerParam.StrokeColor]: iconsObj.dynamicParams.paramTypeStrokeColor,
    [SourceLayerParam.FontSize]: iconsObj.dynamicParams.paramTypeFontSize,
    [SourceLayerParam.ImageSource]: iconsObj.dynamicParams.layerTypeImage,
    [SourceLayerParam.VideoSource]: iconsObj.dynamicParams.layerTypeVideo,
    [SourceLayerParam.AudioSource]: iconsObj.dynamicParams.layerTypeAudio,
    [SourceLayerParam.Click]: iconsObj.dynamicParams.layerTypeClickUrl,
  },
  {
    get: (obj, param: SourceLayerParam) => obj[param] || iconsObj.dynamicParams.paramTypeDefault,
  },
) as Record<SourceLayerParam, string>;

const layerByType = new Proxy<Partial<Record<`${SourceLayerType}`, SourceLayerConfig>>>(
  {
    [SourceLayerType.Text]: {
      icon: iconsObj.dynamicParams.layerTypeText,
      params: [
        SourceLayerParam.TextSource,
        SourceLayerParam.FillColor,
        SourceLayerParam.StrokeColor,
        SourceLayerParam.ColorOverlay,
        SourceLayerParam.Widget,
        SourceLayerParam.Font,
        SourceLayerParam.Subtitles,
      ],
      color: colors.cl_color_orange_deep,
    },
    [SourceLayerType.Shape]: {
      icon: iconsObj.dynamicParams.layerTypeShape,
      params: [
        SourceLayerParam.FillColor,
        SourceLayerParam.StrokeColor,
        SourceLayerParam.Click,
        SourceLayerParam.ColorOverlay,
        SourceLayerParam.Widget,
      ],
      color: colors.cl_color_violet,
    },
    [SourceLayerType.Image]: {
      icon: iconsObj.dynamicParams.layerTypeImage,
      params: [
        SourceLayerParam.ImageSource,
        SourceLayerParam.Click,
        SourceLayerParam.ColorOverlay,
        SourceLayerParam.Widget,
        SourceLayerParam.QR,
      ],
      color: colors.cl_color_blue,
    },
    [SourceLayerType.Null]: {
      icon: iconsObj.dynamicParams.layerTypeNull,
      params: [],
      color: colors.cl_color_black,
    },
    [SourceLayerType.Video]: {
      icon: iconsObj.dynamicParams.layerTypeVideo,
      params: [SourceLayerParam.VideoSource, SourceLayerParam.Click, SourceLayerParam.Widget],
      color: colors.cl_color_yellow,
    },
    [SourceLayerType.Audio]: {
      icon: iconsObj.dynamicParams.layerTypeAudio,
      params: [SourceLayerParam.AudioSource],
      color: colors.cl_color_orange,
    },
    [SourceLayerType.Precomp]: {
      icon: iconsObj.dynamicParams.layerTypePrecomp,
      params: [SourceLayerParam.HTMLContent, SourceLayerParam.Click, SourceLayerParam.Widget],
      color: colors.cl_color_dark_gray,
    },
  },
  {
    get: (obj, param: `${SourceLayerType}`) =>
      obj[param] || {
        icon: iconsObj.dynamicParams.layerTypeDefault,
        params: [],
        color: colors.cl_color_blue,
      },
  },
) as Record<SourceLayerType, SourceLayerConfig>;

const dynamicSourceNameByType: Record<DPDynamicSourceType, DPDynamicSourceTypeName> = {
  [DPDynamicSourceType.Weather]: DPDynamicSourceTypeName.Weather,
  [DPDynamicSourceType.Product]: DPDynamicSourceTypeName.Product,
  [DPDynamicSourceType.Location]: DPDynamicSourceTypeName.Location,
  [DPDynamicSourceType.Campaign]: DPDynamicSourceTypeName.Campaign,
  [DPDynamicSourceType.General]: DPDynamicSourceTypeName.General,
  [DPDynamicSourceType.Content]: DPDynamicSourceTypeName.Content,
};

const dynamicSourceTypeByName: Record<DPDynamicSourceTypeName, DPDynamicSourceType> = {
  [DPDynamicSourceTypeName.Weather]: DPDynamicSourceType.Weather,
  [DPDynamicSourceTypeName.Product]: DPDynamicSourceType.Product,
  [DPDynamicSourceTypeName.Location]: DPDynamicSourceType.Location,
  [DPDynamicSourceTypeName.Campaign]: DPDynamicSourceType.Campaign,
  [DPDynamicSourceTypeName.General]: DPDynamicSourceType.General,
  [DPDynamicSourceTypeName.Content]: DPDynamicSourceType.Content,
};

const dpTypeByWidgetType: Partial<Record<WidgetFieldType, DynamicParametersType>> = {
  [WidgetFieldType.Text]: DynamicParametersType.Text,
  [WidgetFieldType.TextArea]: DynamicParametersType.Text,
  [WidgetFieldType.Date]: DynamicParametersType.Text,
  [WidgetFieldType.Color]: DynamicParametersType.Color,
  [WidgetFieldType.Image]: DynamicParametersType.Image,
  [WidgetFieldType.Video]: DynamicParametersType.Video,
};

const dpTypeToUiName: Record<DynamicParametersType, string> = {
  [DynamicParametersType.Text]: 'Text',
  [DynamicParametersType.Color]: 'Color',
  [DynamicParametersType.Image]: 'Image',
  [DynamicParametersType.Video]: 'Video',
  [DynamicParametersType.Audio]: 'Audio',
  [DynamicParametersType.FloatNumber]: 'Number',
  [DynamicParametersType.Boolean]: 'Boolean',
  [DynamicParametersType.Selector]: 'Selector',
  [DynamicParametersType.Link]: 'Link',
  [DynamicParametersType.Widget]: 'Widget',
  [DynamicParametersType.Font]: 'Font',
  [DynamicParametersType.QR]: 'QR',
  [DynamicParametersType.Subtitles]: 'Subtitles',
};

export {
  sourceParamIcons,
  layerByType,
  defaultMappingFontSize,
  defaultMappingStrikeThrough,
  dynamicSourceNameByType,
  dynamicSourceTypeByName,
  dynamicSourceRegexp,
  clickDefaultSourceValue,
  dpTypeByWidgetType,
  defaultComplexTextModalSize,
  dpTypeToUiName,
};
